import axios from 'axios'

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || 'http://127.0.0.1:5000',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
      config.headers['Authentication-Token'] = token
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('authToken')
      localStorage.removeItem('currentUser')
      window.location.href = '/'
    }
    return Promise.reject(error)
  }
)

// Transform backend user data to frontend format
const transformUserData = (backendUser) => {
  const roles = Array.isArray(backendUser.roles) ? backendUser.roles : [backendUser.roles]
  const primaryRole = roles[0] || 'student'
  
  return {
    id: backendUser.id,
    name: backendUser.full_name || backendUser.email?.split('@')[0] || 'User',
    email: backendUser.email,
    role: primaryRole,
    active: backendUser.active,
    emailVerified: backendUser.email_verified !== false, // Default to true if not specified
    avatar: backendUser.full_name ? 
      backendUser.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : 
      backendUser.email[0].toUpperCase(),
    roles: roles,
    preferences: {
      theme: 'light',
      notifications: true
    },
    createdAt: backendUser.created_at || new Date().toISOString(),
    lastLogin: new Date().toISOString(),
    // Role-specific defaults
    ...(primaryRole === 'student' && {
      grade: backendUser.grade || 'Not specified',
      points: 0,
      level: 1,
      streak: 0,
      totalBadges: 0
    }),
    ...(primaryRole === 'parent' && {
      children: [],
      full_name: backendUser.full_name
    }),
    ...(primaryRole === 'teacher' && {
      classCode: backendUser.class_code || null,
      students: [],
      level: backendUser.level || 'Not specified',
      full_name: backendUser.full_name
    })
  }
}

// Authentication Service
export const authService = {
  async checkEmailAvailability(email) {
    const res = await api.post('/api/check-email', { email });
    return !!res.data.available;
  },
  async requestPasswordReset(email) {
    const res = await api.post('/api/request-password-reset', { email })
    return res.data
  },
  async validateResetToken(token) {
    const res = await api.get('/api/validate-reset-token', { params: { token } })
    return res.data
  },
  async resetPassword(token, password) {
    const res = await api.post('/api/reset-password', { token, password })
    return res.data
  },
  // Login user
  async login(email, password) {
    try {
      
      const response = await api.post('/api/login', { email, password })
      
      if (response.data.token && response.data.user) {
        // Store token
        localStorage.setItem('authToken', response.data.token)
        
        // Transform and store user data
        const transformedUser = transformUserData(response.data.user)
        localStorage.setItem('currentUser', JSON.stringify(transformedUser))
        
        return transformedUser
      } else {
        throw new Error('Invalid response from server')
      }
    } catch (error) {
      console.error('Login error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        code: error.code
      })
      
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error)
      } else if (error.response?.status === 401) {
        throw new Error('Invalid email or password')
      } else if (error.code === 'ECONNREFUSED' || error.code === 'ECONNRESET') {
        throw new Error('Cannot connect to backend server. Please make sure the backend is running on http://localhost:5000')
      } else if (error.code === 'NETWORK_ERROR') {
        throw new Error('Network error. Please check your connection and try again.')
      }
      throw new Error(`Login failed: ${error.message}`)
    }
  },

  // Persist a user + token returned from social login
  persistSocialLogin(payload) {
    try {
      if (payload?.token) {
        localStorage.setItem('authToken', payload.token)
      }
      if (payload?.user) {
        const transformed = {
          id: payload.user.id,
          email: payload.user.email,
          name: payload.user.name || (payload.user.email || '').split('@')[0],
          role: payload.user.role || 'student',
          emailVerified: payload.user.email_verified !== false,
          auth_provider: payload.user.auth_provider,
          profile_picture: payload.user.profile_picture
        }
        localStorage.setItem('currentUser', JSON.stringify(transformed))
        return transformed
      }
      return null
    } catch {
      return null
    }
  },

  // Register user
  async register(userData) {
    try {
      const endpoint = userData.role ? `/api/register/${userData.role}` : '/api/register'
      
      const registrationData = {
        email: userData.email,
        password: userData.password,
        full_name: `${userData.firstName} ${userData.lastName}`,
        ...(userData.role === 'student' && { grade: userData.grade }),
        ...(userData.role === 'teacher' && { level: userData.institution || 'Not specified' }),
        notification_settings: { email: true, progress: true }
      }

      const response = await api.post(endpoint, registrationData)
      
      if (response.data.token && response.data.user) {
        // Store token
        localStorage.setItem('authToken', response.data.token)
        
        // Transform and store user data
        const transformedUser = transformUserData(response.data.user)
        localStorage.setItem('currentUser', JSON.stringify(transformedUser))
        
        return transformedUser
      } else {
        throw new Error('Invalid response from server')
      }
    } catch (error) {
      console.error('Registration error:', error)
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      } else if (error.code === 'ECONNREFUSED') {
        throw new Error('Cannot connect to server. Please try again later.')
      }
      throw new Error('Registration failed. Please try again.')
    }
  },

  // Logout user
  async logout() {
    try {
      const token = localStorage.getItem('authToken')
      if (token) {
        await api.post('/api/logout')
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Always clear local storage
      localStorage.removeItem('authToken')
      localStorage.removeItem('currentUser')
    }
  },

  // Validate current token
  async validateToken() {
    try {
      const token = localStorage.getItem('authToken')
      if (!token) return null

      const response = await api.get('/api/validate-token')
      return response.data
    } catch (error) {
      console.error('Token validation error:', error)
      localStorage.removeItem('authToken')
      localStorage.removeItem('currentUser')
      return null
    }
  },

  // Get current user from localStorage
  getCurrentUser() {
    try {
      const userStr = localStorage.getItem('currentUser')
      return userStr ? JSON.parse(userStr) : null
    } catch (error) {
      console.error('Error getting current user:', error)
      return null
    }
  },

  // Check if user is authenticated
  isAuthenticated() {
    const token = localStorage.getItem('authToken')
    const user = this.getCurrentUser()
    return !!(token && user)
  },

  // Test backend connection
  async testConnection() {
    try {
      const response = await api.get('/')
      return response.data
    } catch (error) {
      console.error('Backend connection test failed:', error)
      throw error
    }
  }
}

// Fallback functions for compatibility with mock data imports
export const loginUser = async (email, password) => {
  
  
  try {
    const user = await authService.login(email, password)
    
    return user
  } catch (error) {
    console.error('❌ Backend authentication failed:', error.message)
    // No mock fallback - throw the actual error for proper handling
    throw error
  }
}

export const registerUser = async (userData) => {
  
  
  try {
    const user = await authService.register(userData)
    
    return user
  } catch (error) {
    console.error('❌ Backend registration failed:', error.message)
    // No mock fallback - throw the actual error for proper handling
    throw error
  }
}

// Export default authService
export default authService 